/* Copyright (c) Trimble Inc. All rights reserved. */

import { useCallback } from "react";
import {
  Button,
  ButtonProps,
  createStyles,
  IconButton,
  makeStyles,
  Theme,
  Tooltip,
} from "@material-ui/core";
import React from "react";
import clsx from "clsx";
import DeleteIcon from "legacy-components/icons/DeleteIcon";

export interface DataRemoverButtonProps
  extends Omit<ButtonProps, "onClick" | "href"> {
  tooltipText?: string;
  skipConfirmationDialog?: boolean;
  icon?: JSX.Element;
  enterButtonText?: string | JSX.Element;
  toggle: () => void;
  onConfirm?: () => void;
  id?: string;
  color?: "inherit" | "primary" | "secondary" | "default";
  disabled?: boolean;
  fromKebab?: boolean;
}
export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    iconButton: {
      borderColor: "#0063A3",
      borderStyle: "solid",
      borderWidth: "1px",
      borderRadius: "4px",
      backgroundColor: "transparent"
    },
  })
);

export const DataRemoverButton: React.FC<DataRemoverButtonProps> = ({
  tooltipText = "Remove",
  skipConfirmationDialog,
  variant = "contained",
  disabled,
  fromKebab,
  color = disabled ? "secondary" : "primary",
  icon = <DeleteIcon style={{ fill: "#900B09", fontSize: "20px" }} />,
  enterButtonText,
  toggle,
  onConfirm,
  id,
  size,
  ...props
}) => {
  const classes = useStyles();
  const onDeleteIconClick = useCallback(() => {
    if (!skipConfirmationDialog) {
      toggle();
      return;
    }
    onConfirm && onConfirm();
  }, [skipConfirmationDialog, toggle, onConfirm]);

  if (!enterButtonText) {
    return (
      <Tooltip data-testid={`${id}-tooltip`} title={tooltipText}>
        <span>
        <IconButton      
          role="button"
          aria-label="data-remover-button"
          onClick={onDeleteIconClick}
          data-testid="delete-icon"
          disabled={disabled}
          size={size === "small" ? "small" : "medium"}
          {...props}
          style={{}}
          className={clsx([classes.iconButton, props?.className])}
        >
          {icon}
        </IconButton>
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip data-testid={`${id}-tooltip`} title={tooltipText}>
      <Button
        aria-label="data-remover-button"
        role="button"
        onClick={onDeleteIconClick}
        data-testid="delete-icon"
        size={size}
        startIcon={icon}
        variant={fromKebab ? "text" : variant}
        disabled={disabled}
        {...props}
      >
        {enterButtonText}
      </Button>
    </Tooltip>
  );
};
